// src/services/strapiService.js
import axios from 'axios';
import { getDemoEvents, getDemoCategories } from '../utils/demoData';
import { getRealEvents, getRealCategories } from '../utils/realData';

// Costante per controllare quale fonte dati utilizzare
// "demo": dati generati casualmente
// "local": dati reali da file JSON locale
// "strapi": dati da API Strapi
const DATA_SOURCE = 'strapi'; // Possibili valori: 'demo', 'local', 'strapi'

//const API_URL = 'http://localhost:1337/api'; // In sviluppo
const API_URL = 'https://cms-finday-v2.onrender.com/api'; // In produzione

/**
 * 🔥 NUOVA FUNZIONE: Recupera tutti i dati con paginazione ricorsiva
 * @param {string} endpoint - Endpoint da chiamare (es: '/events')
 * @param {Object} params - Parametri aggiuntivi per la query
 * @returns {Promise<Array>} - Array con tutti i dati
 */
const fetchAllData = async (endpoint, params = {}) => {
  let allData = [];
  let page = 1;
  let hasMore = true;
  const pageSize = 100; // Usa il limite massimo consentito dal server

  while (hasMore) {
    try {
      const queryParams = new URLSearchParams({
        ...params,
        'pagination[page]': page,
        'pagination[pageSize]': pageSize
      });

      console.log(`Recuperando ${endpoint} - Pagina ${page}...`);
      const response = await fetch(`${API_URL}${endpoint}?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      // Aggiungi i dati alla collezione
      allData = allData.concat(data.data || []);
      
      // Verifica se ci sono altre pagine
      const pagination = data.meta?.pagination;
      if (pagination) {
        hasMore = page < pagination.pageCount;
        console.log(`Pagina ${page}/${pagination.pageCount} - Elementi: ${data.data?.length || 0}`);
      } else {
        // Se non c'è metadata di paginazione, verifica se la pagina è vuota
        hasMore = (data.data || []).length >= pageSize;
      }
      
      page++;
    } catch (error) {
      console.error(`Errore nel recupero pagina ${page}:`, error);
      hasMore = false;
    }
  }

  console.log(`✅ Totale elementi recuperati da ${endpoint}: ${allData.length}`);
  return allData;
};

export const fetchEventsForMap = async () => {
  try {
    // 🔥 USA LA NUOVA FUNZIONE CON PAGINAZIONE RICORSIVA
    const strapiEvents = await fetchAllData('/events', {
      'populate': '*',
      'fields[0]': 'id',
      'fields[1]': 'title',
      'fields[2]': 'latitude',
      'fields[3]': 'longitude',
      'fields[4]': 'location',
      'fields[5]': 'startDate',
      'fields[6]': 'endDate',
      'fields[7]': 'needsReview'
    });
    
    console.log(`✅ Eventi per mappa caricati: ${strapiEvents.length}`);
    return strapiEvents || [];
    
  } catch (error) {
    console.error('Errore nel caricamento eventi per mappa:', error);
    throw error;
  }
};

// Endpoint separato per i dettagli completi quando clicchi su un evento
// Endpoint separato per i dettagli completi quando clicchi su un evento
export const fetchEventDetails = async (eventId) => {
  try {
    const response = await fetch(`${API_URL}/events/${eventId}?populate=*`);
    
    if (!response.ok) {
      throw new Error('Errore nel caricamento dettagli evento');
    }
    
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Errore nel caricamento dettagli evento:', error);
    throw error;
  }
};

export const fetchEvents = async () => {
  // Scegli la fonte dati in base alla configurazione
  switch (DATA_SOURCE) {
    case 'demo':
      console.log('Utilizzando dati evento DEMO');
      return getDemoEvents();
      
    case 'local':
      console.log('Utilizzando dati evento LOCALI');
      return await getRealEvents();
      
    case 'strapi':
      console.log('Utilizzando dati evento da STRAPI');
      try {
        // 🔥 USA LA NUOVA FUNZIONE CON PAGINAZIONE RICORSIVA
        const strapiEvents = await fetchAllData('/events', {
          'populate': '*'
        });
        
        console.log(`✅ Eventi totali recuperati da Strapi: ${strapiEvents.length}`);
        
        // Trasforma i dati nella struttura attesa dall'app
        return strapiEvents.map(item => ({
          id: item.id,
          title: item.title || 'Titolo non disponibile',
          description: item.description || '',
          date: item.startDate || new Date().toISOString(),
          startDate: item.startDate || null,
          endDate: item.endDate || null,
          location: item.location || '',
          address: item.location || '',
          latitude: item.latitude || null, 
          longitude: item.longitude || null,
          
          // ✅ AGGIUNGI QUESTI CAMPI MANCANTI:
          url: item.url || null,
          phone: item.phone || null,
          email: item.email || null,
          ticketPrice: item.ticketPrice !== undefined ? item.ticketPrice : null,
          organizers: item.organizers || [],
          alternativeOrganizer: item.alternativeOrganizer || null,
          isExpired: item.isExpired || false,

          interestCount: item.interestCount || 0,
          
          featured: false,
          isActive: !item.isExpired,
          categories: Array.isArray(item.categories) 
            ? item.categories.map(cat => ({
                id: cat.id,
                name: cat.name || 'Categoria senza nome',
                color: 'red'
              })) 
            : [],
          image: item.images ? item.images : null,
          gallery: []
        }));

      } catch (error) {
        console.error('Errore nel recupero degli eventi da Strapi:', error);
        
        // Log aggiuntivo per avere più dettagli sull'errore
        if (error.response) {
          console.error('Dettagli errore risposta:', {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers
          });
        } else if (error.request) {
          console.error('Errore richiesta (nessuna risposta):', error.request);
        } else {
          console.error('Errore di configurazione:', error.message);
        }
        
        // Fallback ai dati locali in caso di errore
        console.log('Fallback ai dati locali');
        return await getRealEvents();
      }

    default:
      console.error('Fonte dati non riconosciuta');
      return [];
  }
};

export const fetchCategories = async () => {
  // Scegli la fonte dati in base alla configurazione
  switch (DATA_SOURCE) {
    case 'demo':
      console.log('Utilizzando categorie DEMO');
      return getDemoCategories();
      
    case 'local':
      console.log('Utilizzando categorie LOCALI');
      return await getRealCategories();
      
    case 'strapi':
      console.log('Utilizzando categorie da STRAPI');
      try {
        // 🔥 USA LA NUOVA FUNZIONE CON PAGINAZIONE RICORSIVA
        const strapiCategories = await fetchAllData('/categories', {
          'populate': '*'
        });
        
        console.log(`✅ Categorie totali ricevute da Strapi: ${strapiCategories.length}`);
        
        // Trasforma i dati nella struttura attesa dall'app
        return strapiCategories.map(item => ({
          id: item.id,
          name: item.name || 'Categoria senza nome',
          description: item.description || '',
          color: 'red', // Default color perché non esiste nella risposta
          icon: null // Non c'è un campo icon nella risposta
        }));
      } catch (error) {
        console.error('Errore nel recupero delle categorie da Strapi:', error);
        
        // Log aggiuntivo per avere più dettagli sull'errore
        if (error.response) {
          console.error('Dettagli errore risposta:', {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers
          });
        } else if (error.request) {
          console.error('Errore richiesta (nessuna risposta):', error.request);
        } else {
          console.error('Errore di configurazione:', error.message);
        }
        
        // Fallback alle categorie locali in caso di errore
        console.log('Fallback alle categorie locali');
        return await getRealCategories();
      }

    default:
      console.error('Fonte dati non riconosciuta');
      return [];
  }
};

/**
 * 🆕 NUOVE FUNZIONI PER GESTIRE GLI ORGANIZZATORI
 */

/**
 * Recupera eventi per ID organizzatore
 * @param {number|string} organizerId - ID dell'organizzatore
 * @returns {Promise<Object>} - Oggetto con eventi e dati organizzatore
 */
export const getEventsByOrganizer = async (organizerId) => {
  try {
    console.log('Recupero eventi per organizzatore ID:', organizerId);
    
    // 🔧 USA LA NUOVA ROUTE PERSONALIZZATA
    const response = await fetch(
      `${API_URL}/events/by-organizer/${organizerId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Errore risposta API:', errorData);
      throw new Error('Errore nel recupero degli eventi dell\'organizzatore');
    }

    const responseData = await response.json();
    
    // Trasforma gli eventi nella struttura attesa dall'app
    const transformedEvents = responseData.data.map(item => ({
      id: item.id,
      title: item.title || 'Titolo non disponibile',
      description: item.description || '',
      date: item.startDate || new Date().toISOString(),
      startDate: item.startDate || null,
      endDate: item.endDate || null,
      location: item.location || '',
      address: item.location || '',
      latitude: item.latitude || null,
      longitude: item.longitude || null,
      url: item.url || null,
      phone: item.phone || null,
      email: item.email || null,
      ticketPrice: item.ticketPrice !== undefined ? item.ticketPrice : null,
      organizers: item.organizers || [],
      alternativeOrganizer: item.alternativeOrganizer || null,
      isExpired: item.isExpired || false,
      interestCount: item.interestCount || 0,
      featured: false,
      isActive: !item.isExpired,
      categories: Array.isArray(item.categories) 
        ? item.categories.map(cat => ({
            id: cat.id,
            name: cat.name || 'Categoria senza nome',
            color: 'red'
          })) 
        : [],
      image: item.images ? item.images : null,
      gallery: []
    }));

    return {
      events: transformedEvents,
      organizer: responseData.organizer
    };

  } catch (error) {
    console.error('Errore nel recupero eventi organizzatore:', error);
    throw error;
  }
};

/**
 * Cerca eventi per nome organizzatore alternativo
 * @param {string} organizerName - Nome dell'organizzatore alternativo
 * @returns {Promise<Object>} - Oggetto con eventi e dati organizzatore
 */
export const searchEventsByOrganizerName = async (organizerName) => {
  try {
    // 🔥 USA LA NUOVA FUNZIONE CON PAGINAZIONE RICORSIVA
    const strapiEvents = await fetchAllData('/events', {
      'filters[alternativeOrganizer][$eq]': organizerName,
      'populate': '*'
    });

    // Trasforma gli eventi nella struttura attesa dall'app
    const transformedEvents = strapiEvents.map(item => ({
      id: item.id,
      title: item.title || 'Titolo non disponibile',
      description: item.description || '',
      date: item.startDate || new Date().toISOString(),
      startDate: item.startDate || null,
      endDate: item.endDate || null,
      location: item.location || '',
      address: item.location || '',
      latitude: item.latitude || null,
      longitude: item.longitude || null,
      url: item.url || null,
      phone: item.phone || null,
      email: item.email || null,
      ticketPrice: item.ticketPrice !== undefined ? item.ticketPrice : null,
      organizers: item.organizers || [],
      alternativeOrganizer: item.alternativeOrganizer || null,
      isExpired: item.isExpired || false,
      featured: false,
      isActive: !item.isExpired,
      categories: Array.isArray(item.categories) 
        ? item.categories.map(cat => ({
            id: cat.id,
            name: cat.name || 'Categoria senza nome',
            color: 'red'
          })) 
        : [],
      image: item.images ? item.images : null,
      gallery: []
    }));

    return {
      events: transformedEvents,
      organizer: { name: organizerName }
    };

  } catch (error) {
    console.error('Errore nella ricerca eventi per nome organizzatore:', error);
    throw error;
  }
};

/**
 * Normalizza i dati degli eventi per garantire coerenza tra diverse fonti
 * @param {Array} events - Array di eventi da normalizzare
 * @returns {Array} - Array di eventi normalizzati
 */
export const normalizeEventData = (events) => {
  if (!events || !Array.isArray(events)) return [];
  
  return events.map(event => ({
    id: event.id,
    title: event.title || 'Evento senza titolo',
    description: event.description || '',
    date: event.date ? new Date(event.date) : new Date(),
    endDate: event.endDate ? new Date(event.endDate) : null,
    location: event.location || '',
    city: event.city || (event.address ? extractCityFromAddress(event.address) : ''),
    address: event.address || event.location || '',
    latitude: typeof event.latitude === 'number' ? event.latitude : 0,
    longitude: typeof event.longitude === 'number' ? event.longitude : 0,
    price: event.price || 'Prezzo non disponibile',
    categories: Array.isArray(event.categories) ? event.categories : [],
    image: event.image || null,
    gallery: Array.isArray(event.gallery) ? event.gallery : [],
    organizer: event.organizer || '',
    website: event.website || '',
    contact: event.contact || '',
    capacity: typeof event.capacity === 'number' ? event.capacity : 0,
    featured: !!event.featured,
    isActive: event.isActive !== false
  }));
};

/**
 * Estrae il nome della città da un indirizzo completo
 * @param {string} address - Indirizzo completo
 * @returns {string} - Nome della città
 */
const extractCityFromAddress = (address) => {
  if (!address) return '';
  
  // Cerca di estrarre un codice postale italiano (5 cifre) seguito da città
  const match = address.match(/\d{5}\s+([^,]+)/);
  if (match && match[1]) {
    return match[1].trim();
  }
  
  // Se non riesce, prova a dividere l'indirizzo in parti e prendi la penultima
  const parts = address.split(',').map(part => part.trim());
  if (parts.length >= 2) {
    return parts[parts.length - 2];
  }
  
  return '';
};

/**
 * Recupera un singolo evento per ID
 * @param {number|string} id - ID dell'evento
 * @returns {Promise<Object|null>} - Evento o null se non trovato
 */
export const fetchEventById = async (id) => {
  try {
    console.log('fetchEventById chiamata con ID:', id);
    
    // Prima prova con fetchEventDetails (più efficiente)
    const eventDetails = await fetchEventDetails(id);
    
    if (eventDetails) {
      console.log('Evento trovato con fetchEventDetails:', eventDetails);
      
      // Trasforma i dati nella struttura attesa dall'app
      return {
        id: eventDetails.id,
        title: eventDetails.title || 'Titolo non disponibile',
        description: eventDetails.description || '',
        date: eventDetails.startDate || new Date().toISOString(),
        startDate: eventDetails.startDate || null,
        endDate: eventDetails.endDate || null,
        location: eventDetails.location || '',
        address: eventDetails.location || '',
        latitude: eventDetails.latitude || null,
        longitude: eventDetails.longitude || null,
        url: eventDetails.url || null,
        phone: eventDetails.phone || null,
        email: eventDetails.email || null,
        ticketPrice: eventDetails.ticketPrice !== undefined ? eventDetails.ticketPrice : null,
        organizers: eventDetails.organizers || [],
        alternativeOrganizer: eventDetails.alternativeOrganizer || null,
        isExpired: eventDetails.isExpired || false,
        interestCount: eventDetails.interestCount || 0,
        featured: false,
        isActive: !eventDetails.isExpired,
        categories: Array.isArray(eventDetails.categories) 
          ? eventDetails.categories.map(cat => ({
              id: cat.id,
              name: cat.name || 'Categoria senza nome',
              color: 'red'
            })) 
          : [],
        image: eventDetails.images ? eventDetails.images : null,
        gallery: []
      };
    }
    
    return null;
    
  } catch (error) {
    console.error('Errore in fetchEventById:', error);
    
    // Fallback: cerca nell'elenco completo degli eventi
    try {
      console.log('Tentativo fallback con fetchEvents...');
      const allEvents = await fetchEvents();
      const eventId = parseInt(id, 10);
      const foundEvent = allEvents.find(event => event.id === eventId);
      
      if (foundEvent) {
        console.log('Evento trovato con fallback:', foundEvent);
        return foundEvent;
      }
      
    } catch (fallbackError) {
      console.error('Errore anche nel fallback:', fallbackError);
    }
    
    throw new Error('Evento non trovato');
  }
};

/**
 * Recupera eventi filtrati per categoria
 * @param {number|string} categoryId - ID della categoria
 * @returns {Promise<Array>} - Array di eventi filtrati
 */
export const fetchEventsByCategory = async (categoryId) => {
  const events = await fetchEvents();
  const catId = parseInt(categoryId, 10);
  
  return events.filter(event => 
    event.categories && Array.isArray(event.categories) &&
    event.categories.some(cat => cat.id === catId)
  );
};

/**
 * Recupera eventi filtrati per città
 * @param {string} city - Nome della città
 * @returns {Promise<Array>} - Array di eventi filtrati
 */
export const fetchEventsByCity = async (city) => {
  const events = await fetchEvents();
  const cityName = city.toLowerCase();
  
  return events.filter(event => 
    (event.city && event.city.toLowerCase() === cityName) ||
    (event.address && event.address.toLowerCase().includes(cityName))
  );
};

/**
 * Cerca eventi in base a un termine di ricerca
 * @param {string} searchTerm - Termine di ricerca
 * @returns {Promise<Array>} - Array di eventi che corrispondono alla ricerca
 */
export const searchEvents = async (searchTerm) => {
  const events = await fetchEvents();
  if (!searchTerm || typeof searchTerm !== 'string') return events;
  
  const term = searchTerm.toLowerCase().trim();
  if (term === '') return events;
  
  return events.filter(event => {
    const searchableFields = [
      event.title,
      event.description,
      event.city,
      event.location,
      event.address,
      event.organizer
    ].filter(Boolean); // Rimuove valori null/undefined
    
    return searchableFields.some(field => 
      field.toLowerCase().includes(term)
    );
  });
};

/**
 * Recupera eventi futuri (dalla data odierna in poi)
 * @returns {Promise<Array>} - Array di eventi futuri
 */
export const fetchUpcomingEvents = async () => {
  const events = await fetchEvents();
  const now = new Date();
  
  return events
    .filter(event => {
      // Se l'evento ha una data di fine e quella è nel futuro, includi l'evento
      if (event.endDate && new Date(event.endDate) >= now) {
        return true;
      }
      // Altrimenti controlla solo la data di inizio
      return new Date(event.date) >= now;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Recupera eventi in corso (la cui data di inizio è nel passato ma la data di fine è nel futuro)
 * @returns {Promise<Array>} - Array di eventi in corso
 */
export const fetchOngoingEvents = async () => {
  const events = await fetchEvents();
  const now = new Date();
  
  return events.filter(event => {
    const startDate = new Date(event.date);
    const endDate = event.endDate ? new Date(event.endDate) : null;
    
    // Se non c'è data di fine, l'evento non è considerato in corso se è già iniziato
    if (!endDate) return false;
    
    // L'evento è in corso se la data attuale è tra la data di inizio e la data di fine
    return startDate <= now && now <= endDate;
  });
};

/**
 * Recupera eventi in evidenza
 * @param {number} limit - Numero massimo di eventi da recuperare
 * @returns {Promise<Array>} - Array di eventi in evidenza
 */
export const fetchFeaturedEvents = async (limit = 6) => {
  const events = await fetchEvents();
  
  // Prima prendi gli eventi contrassegnati come featured
  let featuredEvents = events.filter(event => event.featured === true);
  
  // Se non ci sono abbastanza eventi featured, aggiungi altri eventi futuri
  if (featuredEvents.length < limit) {
    const upcomingEvents = await fetchUpcomingEvents();
    const additionalEvents = upcomingEvents
      .filter(event => !featuredEvents.some(fe => fe.id === event.id))
      .slice(0, limit - featuredEvents.length);
    
    featuredEvents = [...featuredEvents, ...additionalEvents];
  }
  
  // Limita al numero richiesto
  return featuredEvents.slice(0, limit);
};

/**
 * Recupera eventi nelle vicinanze di coordinate geografiche
 * @param {number} lat - Latitudine
 * @param {number} lng - Longitudine
 * @param {number} radiusKm - Raggio in chilometri
 * @returns {Promise<Array>} - Array di eventi ordinati per distanza
 */
export const fetchNearbyEvents = async (lat, lng, radiusKm = 20) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    console.error('Coordinate non valide');
    return [];
  }
  
  const events = await fetchEvents();
  
  // Calcola la distanza usando la formula di Haversine
  return events
    .filter(event => {
      if (typeof event.latitude !== 'number' || typeof event.longitude !== 'number') {
        return false;
      }
      
      const distance = calculateDistance(
        lat, lng, 
        event.latitude, event.longitude
      );
      return distance <= radiusKm;
    })
    .map(event => {
      const distance = calculateDistance(lat, lng, event.latitude, event.longitude);
      return { ...event, distance };
    })
    .sort((a, b) => a.distance - b.distance);
};

/**
 * Calcola la distanza in km tra due punti geografici usando la formula di Haversine
 * @param {number} lat1 - Latitudine del primo punto
 * @param {number} lon1 - Longitudine del primo punto
 * @param {number} lat2 - Latitudine del secondo punto
 * @param {number} lon2 - Longitudine del secondo punto
 * @returns {number} - Distanza in chilometri
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Raggio della Terra in km
  const dLat = degToRad(lat2 - lat1);
  const dLon = degToRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

/**
 * Converte gradi in radianti
 * @param {number} deg - Gradi
 * @returns {number} - Radianti
 */
const degToRad = (deg) => {
  return deg * (Math.PI/180);
};

/**
 * Ottiene la fonte dati attuale
 * @returns {string} - 'demo', 'local' o 'strapi'
 */
export const getDataSource = () => {
  return DATA_SOURCE;
};

/**
 * Imposta la fonte dati
 * @param {string} source - 'demo', 'local' o 'strapi'
 */
export const setDataSource = (source) => {
  if (['demo', 'local', 'strapi'].includes(source)) {
    // Questo è solo per simulare il cambio. In una vera app, dovresti 
    // usare una variabile di ambiente o un sistema di configurazione.
    console.log(`Cambio fonte dati da ${DATA_SOURCE} a ${source}`);
    // DATA_SOURCE = source; // Questa riga non funzionerà poiché DATA_SOURCE è una costante
    // In una vera implementazione, useresti uno state manager come Redux
  } else {
    console.error('Fonte dati non valida');
  }
};

/**
 * Recupera eventi raggruppati per città
 * @returns {Promise<Object>} - Oggetto con città come chiavi e array di eventi come valori
 */
export const fetchEventsByLocation = async () => {
  const events = await fetchEvents();
  const eventsByLocation = {};
  
  events.forEach(event => {
    if (event.city) {
      if (!eventsByLocation[event.city]) {
        eventsByLocation[event.city] = [];
      }
      eventsByLocation[event.city].push(event);
    }
  });
  
  return eventsByLocation;
};

/**
 * Recupera eventi popolari (implementazione di esempio)
 * @param {number} limit - Numero massimo di eventi da recuperare
 * @returns {Promise<Array>} - Array di eventi popolari
 */
export const fetchPopularEvents = async (limit = 5) => {
  // In una vera implementazione, avresti una logica per determinare
  // la popolarità, come il numero di visite, preferiti, ecc.
  // Per ora, prendiamo semplicemente alcuni eventi a caso
  const events = await fetchEvents();
  
  // Simuliamo la popolarità mescolando casualmente gli eventi
  const shuffled = [...events].sort(() => 0.5 - Math.random());
  
  return shuffled.slice(0, limit);
};

// Esporta tutte le funzioni per avere un'API completa
export default {
  fetchEvents,
  fetchCategories,
  fetchEventsForMap,
  fetchEventDetails,
  fetchEventById,
  fetchEventsByCategory,
  fetchEventsByCity,
  searchEvents,
  fetchUpcomingEvents,
  fetchOngoingEvents,
  fetchFeaturedEvents,
  fetchNearbyEvents,
  fetchEventsByLocation,
  fetchPopularEvents,
  getDataSource,
  // 🆕 Nuove funzioni per gli organizzatori
  getEventsByOrganizer,
  searchEventsByOrganizerName
};
